import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useEffect, useMemo, useReducer, useState} from 'react';

import AnalysisTemplateSelector from 'analysis-tool/templates/AnalysisTemplateSelector';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {GetAnalysisTemplate} from 'analysis-tool/templates/graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import AnalysisTemplateInputTableColumnMapper from 'analysis-tool/templates/table-mapping/AnalysisTemplateInputTableColumnMapper';
import useAnalysisTemplateSelectorState, {
  TemplateApplyStateType,
} from 'analysis-tool/templates/useAnalysisTemplateSelectorState';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Checkbox from 'ui-library/Checkbox';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {LabelStyle, Size} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';

import {AddComponentButton, RemoveComponentButton} from '../RunSchemaEditorActionButton';
import {ClientAutomationOutputFileConfigSpec} from '../RunSchemaSpecConstants';
import {
  convertStringToColumnTypeMetadatas,
  DatasetColumnTypeMetadata,
  DatasetConfigModalState,
  INITIAL_STATE,
  PRIMITIVE_COLUMN_TYPES,
  PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME,
  PRIMITIVE_COLUMN_TYPE_TO_LEAF_TYPE,
  StepKey,
} from './DatasetConfigConstants';
import reducer, {DatasetConfigModalAction, DatasetConfigModalActionType} from './datasetConfigModalReducer';

export interface DatasetConfigChanges {
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>;
  analysisTemplate: string | null;
}

export interface DatasetConfigModalProps {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onRequestClose: () => void;
  onRequestSubmit: (configChanges: DatasetConfigChanges) => void | Promise<void>;
}

function getStepButtons(
  state: DatasetConfigModalState,
  dispatch: Dispatch<DatasetConfigModalAction>,
  onRequestSubmit: (configChanges: DatasetConfigChanges) => void | Promise<void>
): Array<ModalButton> {
  if (state.currentStepKey === StepKey.COLUMNS) {
    return [
      {
        text: 'Next',
        submit: true,
        onClick: () => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.ANALYSIS_TEMPLATE,
            columnTypeMetadatas: state.columnTypeMetadatas,
          });
        },
      },
    ];
  } else {
    // StepKey.ANALYSIS_TEMPLATE case
    return [
      {
        text: 'Back',
        submit: false,
        onClick: () => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.COLUMNS,
          });
        },
      },
      {
        text: 'Done',
        submit: true,
        onClick: async () => {
          await onRequestSubmit({
            columnTypeMetadatas: state.columnTypeMetadatas,
            analysisTemplate: state.analysisTemplate,
          });
        },
      },
    ];
  }
}

function getHelpText(currentStepKey: StepKey): string {
  if (currentStepKey === StepKey.COLUMNS) {
    return `Select the columns you want to include in your dataset, and their corresponding data types.`;
  } else if (currentStepKey === StepKey.ANALYSIS_TEMPLATE) {
    return 'Optionally, select an analysis template to be automatically applied to the dataset generated by this run. The template selector is pre-filtered to show only templates with a single variable input, and only "Required" dataset columns can be mapped.';
  } else {
    return '';
  }
}

function getStepsList(): Array<{key: StepKey; title: string}> {
  const stepsList: Array<{key: StepKey; title: string}> = [];

  stepsList.push({
    key: StepKey.COLUMNS,
    title: 'Set dataset columns',
  });
  stepsList.push({
    key: StepKey.ANALYSIS_TEMPLATE,
    title: 'Set analysis template',
  });
  return stepsList;
}

interface DatasetColumnConfigPageProps {
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onAddColumnTypeMetadata: (columnTypeMetadata: DatasetColumnTypeMetadata) => void;
  onUpdateColumnTypeMetadata: (index: number, updatedColumnTypeMetadata: DatasetColumnTypeMetadata) => void;
  onRemoveColumnTypeMetadata: (index: number) => void;
}

function DatasetColumnConfigPage(props: DatasetColumnConfigPageProps): JSX.Element {
  return (
    <>
      <Row className={css(styles.headerRow)}>
        <Col md={6} className={css(styles.headerColumn)}>
          Column
        </Col>
        <Col md={4} className={css(styles.headerColumn)}>
          Column type
        </Col>
        <Col md={1} className={css(styles.headerColumn)}>
          Required?
        </Col>
        <Col md={1} className={css(styles.headerColumn)} />
      </Row>
      {props.columnTypeMetadatas.map((columnTypeMetadata, index) => (
        <Row key={index} className={css(styles.row)}>
          <Col md={6} className={css(styles.column)}>
            <TextField
              label={`Column name ${index}`}
              labelStyle={LabelStyle.HIDDEN}
              value={columnTypeMetadata.name ?? ''}
              size={Size.MEDIUM}
              onChange={(e) => {
                props.onUpdateColumnTypeMetadata(index, {
                  ...columnTypeMetadata,
                  name: e.target.value,
                });
              }}
              placeholder="Column name"
            />
          </Col>
          <Col md={4} className={css(styles.column)}>
            <SelectDropdown
              displayName={
                columnTypeMetadata.columnType
                  ? PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[columnTypeMetadata.columnType]
                  : 'Choose column type...'
              }
              dataTestAttrs={{
                component: 'SetColumnsMapFieldsSection',
                element: 'selectDropdown',
              }}
              menu={
                <SearchableMenu
                  selectedValue={columnTypeMetadata.columnType ?? 'select'}
                  onSelect={(value) => {
                    props.onUpdateColumnTypeMetadata(index, {...columnTypeMetadata, columnType: value});
                  }}
                  optionDatas={PRIMITIVE_COLUMN_TYPES.map(
                    (type: string): OptionData => ({
                      name: PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[type],
                      value: type,
                    })
                  )}
                  height={Menu.HEIGHTS.MEDIUM}
                  width={Menu.WIDTHS.FULL}
                />
              }
            />
          </Col>
          <Col md={1} className={css(styles.column)}>
            <Checkbox
              checked={columnTypeMetadata.isRequired ?? true}
              onChange={() => {
                const current = columnTypeMetadata.isRequired ?? true;
                props.onUpdateColumnTypeMetadata(index, {...columnTypeMetadata, isRequired: !current});
              }}
            />
          </Col>
          <Col md={1} className={css(styles.column)}>
            <RemoveComponentButton
              label="Remove"
              onClick={() => {
                props.onRemoveColumnTypeMetadata(index);
              }}
            />
          </Col>
        </Row>
      ))}
      <Row className={css(styles.row)}>
        <Col md={6} className={css(styles.column)}>
          <AddComponentButton
            label="Add Column"
            onClick={() => {
              props.onAddColumnTypeMetadata({isRequired: true});
            }}
          />
          <a
            className={css(styles.headerColumn)}
            onClick={() => {
              props.onAddColumnTypeMetadata({isRequired: true});
            }}
          >
            Add Column
          </a>
        </Col>
      </Row>
    </>
  );
}

interface AnalysisTemplatePageProps {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  setColumnMapping: (columnMapping: Record<string, string>) => void;
  state: DatasetConfigModalState;
  templateApplyState: TemplateApplyStateType;
  setSelectedTemplate: (selectedTemplate: AnalysisTemplateGraphQL | null) => void;
}

function filterTemplate(template: AnalysisTemplateGraphQL): boolean {
  return template.dataFrameVariables.length === 1;
}

function columnTypeMetadatasToDataFrameColumnDefs(
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>
): Array<DataFrameColumnDef> {
  return columnTypeMetadatas
    .filter((columnTypeMetadata) => columnTypeMetadata.isRequired && columnTypeMetadata.name)
    .map((columnTypeMetadata) => ({
      displayName: columnTypeMetadata.name!,
      systemName: columnTypeMetadata.name!,
      type: {leafType: PRIMITIVE_COLUMN_TYPE_TO_LEAF_TYPE[columnTypeMetadata.columnType ?? 'text']},
    }));
}

function AnalysisTemplatePage(props: AnalysisTemplatePageProps): JSX.Element {
  const {outputFileConfig, setColumnMapping, setSelectedTemplate, state, templateApplyState} = props;
  const shouldSkip = Boolean(templateApplyState.selectedTemplate || !outputFileConfig.analysisTemplate);
  const [queried, setQueried] = useState(shouldSkip);

  const queryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    skip: queried || shouldSkip,
    variables: {analysisTemplateId: outputFileConfig.analysisTemplate},
  });

  useEffect(() => {
    if (!queried) {
      const queriedTemplate =
        queryResult.type === QueryResultType.SUCCESS && !isInaccessible(queryResult.data.analysisTemplate)
          ? queryResult.data.analysisTemplate
          : null;
      if (queriedTemplate != null) {
        setSelectedTemplate(queriedTemplate);
        setQueried(true);
      }
    }
  }, [queried, queryResult, setSelectedTemplate]);

  const inputTableColumns = columnTypeMetadatasToDataFrameColumnDefs(state.columnTypeMetadatas);
  const columnMapping = Object.fromEntries(
    state.columnTypeMetadatas
      .filter((metadata) => metadata.name && metadata.templateColumnName)
      .map((metadata) => [metadata.templateColumnName!, metadata.name!])
  );

  const templateSelector = !queried ? (
    <CenteredHorizontalThrobber />
  ) : (
    <AnalysisTemplateSelector
      value={templateApplyState.selectedTemplate}
      onSelect={setSelectedTemplate}
      filterTemplate={filterTemplate}
      includeNone={true}
    />
  );

  const selectedTemplateDataFrame =
    templateApplyState.inputTables.length > 0
      ? templateApplyState.inputTables[0].templateLineageNode.data.dataFrame
      : null;

  return (
    <AnalysisTemplateInputTableColumnMapper
      columnMapping={columnMapping}
      setColumnMapping={setColumnMapping}
      inputTableColumns={inputTableColumns}
      inputTableColumnsHeader="Dataset column"
      inputTableDataFrameIdForPreview={null}
      inputTableSelector={templateSelector}
      inputTableSelectorLabel="Analysis template"
      templateTableColumnsHeader="Template column"
      templateTableDataFrame={selectedTemplateDataFrame}
      templateTableLabel="Template table"
      isInputTableColumnUnique={true}
    />
  );
}

export default function DatasetConfigModal(props: DatasetConfigModalProps): JSX.Element {
  const initialColumnTypeMetadatas = convertStringToColumnTypeMetadatas(
    props.outputFileConfig.datasetConfigJson
  );
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    analysisTemplate: props.outputFileConfig.analysisTemplate ?? null,
    columnTypeMetadatas: initialColumnTypeMetadatas,
  });

  const buttons = getStepButtons(state, dispatch, props.onRequestSubmit);
  const steps = useMemo(() => getStepsList(), []);

  function setColumnMapping(columnMapping: Record<string, string>): void {
    const flippedColumnMapping: Record<string, string> = Object.fromEntries(
      Object.entries(columnMapping).map(([key, value]) => [value, key])
    );
    const newMetadatas: Array<DatasetColumnTypeMetadata> = [];
    for (const metadata of state.columnTypeMetadatas) {
      if (metadata.name && flippedColumnMapping[metadata.name]) {
        metadata.templateColumnName = flippedColumnMapping[metadata.name];
      } else {
        delete metadata.templateColumnName;
      }
      newMetadatas.push(metadata);
    }
    dispatch({
      type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
      columnTypeMetadatas: newMetadatas,
    });
  }

  const [templateApplyState, , setSelectedTemplateBase] = useAnalysisTemplateSelectorState(
    props.outputFileConfig.name
  );

  function setSelectedTemplate(selectedTemplate: AnalysisTemplateGraphQL | null): void {
    setSelectedTemplateBase(selectedTemplate);
    dispatch({
      type: DatasetConfigModalActionType.UPDATE_ANALYSIS_TEMPLATE,
      analysisTemplate: selectedTemplate ? selectedTemplate.id : null,
    });
  }

  return (
    <Modal
      buttons={buttons}
      closeableOnEscapeKey={false}
      mediumLarge={true}
      fullWidth={state.currentStepKey === StepKey.ANALYSIS_TEMPLATE}
      onRequestClose={props.onRequestClose}
      title="Configure dataset"
    >
      <StepNav<StepKey>
        steps={steps}
        statusByStep={state.statusByStep}
        currentStepKey={state.currentStepKey}
        onUpdateCurrentStep={(nextStepKey: StepKey) => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey,
          });
        }}
        variant={StepNavVariant.FLAT}
      />
      <p className={css(styles.helpText)}>{getHelpText(state.currentStepKey)}</p>
      {state.currentStepKey === StepKey.COLUMNS ? (
        <DatasetColumnConfigPage
          columnTypeMetadatas={state.columnTypeMetadatas}
          outputFileConfig={props.outputFileConfig}
          onAddColumnTypeMetadata={(columnTypeMetadata) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [...state.columnTypeMetadatas, columnTypeMetadata],
            });
          }}
          onUpdateColumnTypeMetadata={(index, updatedColumnTypeMetadata) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [
                ...state.columnTypeMetadatas.slice(0, index),
                updatedColumnTypeMetadata,
                ...state.columnTypeMetadatas.slice(index + 1),
              ],
            });
          }}
          onRemoveColumnTypeMetadata={(index) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [
                ...state.columnTypeMetadatas.slice(0, index),
                ...state.columnTypeMetadatas.slice(index + 1),
              ],
            });
          }}
        />
      ) : (
        <AnalysisTemplatePage
          outputFileConfig={props.outputFileConfig}
          setColumnMapping={setColumnMapping}
          state={state}
          templateApplyState={templateApplyState}
          setSelectedTemplate={setSelectedTemplate}
        />
      )}
    </Modal>
  );
}

export const styles = StyleSheet.create({
  helpText: {
    'font-style': 'italic',
    padding: '10px 0 20px',
  },
  row: {
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  headerRow: {
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderBottomWidth: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  column: {
    padding: '15px',
  },
  headerColumn: {
    marginBottom: '8px',
  },
});
